import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import ErrorBoundary from '@/components/ErrorBoundary';
import { Loading } from '@/components/loaders';
import { SMS_TEMPLATE_DELETE_FLOW_NAME } from '@/features/campaigns/v2/Sms/constants';
import { useSmsWorkspace } from '@/features/campaigns/v2/Sms/context/SmsWorkspaceContext';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import RouteConstants from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { ToastType } from '@/models/toast';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { Button } from '@/lib';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

const DeleteInit = ({
  workspaceId,
  smsConnectorFlowId,
  connectorInstanceId,
  connectorId,
}: {
  workspaceId: string;
  smsConnectorFlowId?: string;
  connectorInstanceId?: string;
  connectorId: string;
}) => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation('campaign');
  const appDispatch = useAppDispatch();
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [prepareError, setPrepareError] = useState(false);
  const history = useHistory();
  const [prepareStep] = usePrepareConnectorFlowStepMutation();
  const [formData, setFormData] = useState<FlowFormData>({});
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const [createFlowInstance, { isLoading: isCreatingFlowInstance }] = useCreateFlowInstanceMutation();

  const fetchPrepareData = useCallback(async () => {
    if (!connectorInstanceId || !smsConnectorFlowId || !audienceId) {
      return;
    }

    setPrepareError(false);

    try {
      const response = await prepareStep({
        workspaceId,
        flowId: smsConnectorFlowId,
        audienceId,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);
      setPrepareResult(translatedResponse?.configuration?.elements?.params as PrepareStepResult);
    } catch {
      setPrepareError(true);
      setPrepareResult(null);
    }
  }, [audienceId, connectorInstanceId, smsConnectorFlowId, prepareStep, currentLanguage, workspaceId]);

  useEffect(() => {
    void fetchPrepareData();
  }, [fetchPrepareData]);

  const handleDeleteTemplate = async (opts?: CreateTemplateOpts) => {
    const instance = instances?.find((inst) => inst.connectorId === connectorId);

    if (!instance) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('missingAudience.toast'));
      appDispatch(showToast({ type: ToastType.ERROR, title: message }));
      return { success: false, message };
    }

    const flowParams = opts?.flowParams ?? formData;
    const flowMappings = opts?.flowMappings ?? mappingData;

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: smsConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(showToast({ title: String(t('smsWorkspace.templates.deleteSuccess')), type: ToastType.SUCCESS }));
      history.push(RouteConstants.smsTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(showToast({ type: ToastType.ERROR, title: errorMessage }));
      return { success: false, message: errorMessage };
    }
  };

  const multiStepFlowFormProps = {
    audienceId: audienceId!,
    connectorInstanceId: connectorInstanceId!,
    connectorFlowId: smsConnectorFlowId!,
    paramsConfig: prepareResult || {},
    onComplete: (allParams: FlowFormData) => {
      setFormData(allParams);
      setMappingData({});
      void handleDeleteTemplate({ flowParams: allParams, flowMappings: {}, fromFlowComplete: true });
    },
  };

  const isConnectorReady = !!connectorInstanceId && !!smsConnectorFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult && !prepareError;

  if (isAudienceLoading || isPreparePending) {
    return <Loading />;
  }

  if (isAudienceSettled && !hasAudience) {
    return <AudienceRequiredEmptyState />;
  }

  if (prepareError) {
    return (
      <div className='space-y-4'>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.templates.deleteTemplatesHub')}</h2>
        <div className='flex flex-col items-start gap-3'>
          <p className='text-sm text-gray-600'>{t('smsWorkspace.templates.failedToLoadTemplate')}</p>
          <Button intent='secondary' onClick={() => void fetchPrepareData()} text={t('smsWorkspace.templates.tryAgain')} />
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <h2 className='text-3xl font-semibold text-gray-900'>{t('smsWorkspace.templates.deleteTemplatesHub')}</h2>
      <ErrorBoundary>{prepareResult ? <MultiStepFlowForm {...multiStepFlowFormProps} isInAction={isCreatingFlowInstance} /> : null}</ErrorBoundary>
    </div>
  );
};

export const SmsTemplateDeleteWorkaround = () => {
  const { id: workspaceId } = useWorkspace();
  const { connectorId } = useSmsWorkspace();
  const { data: connectorInstances, isLoading: isConnectorInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const { data: connectorFlows, isLoading: isConnectorFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId, queryParams: { showUserJourney: true } },
    { skip: !workspaceId }
  );

  const smsConnectorFlow = connectorFlows?.find((flow) => flow.name === SMS_TEMPLATE_DELETE_FLOW_NAME && flow.connectorId === connectorId);
  const smsConnectorFlowId = smsConnectorFlow?.id;
  const connectorInstanceId = connectorInstances?.find((instance) => instance.connectorId === connectorId)?.id;

  if (isConnectorInstancesLoading || isConnectorFlowsLoading) {
    return <Loading />;
  }

  return (
    <DeleteInit workspaceId={workspaceId} smsConnectorFlowId={smsConnectorFlowId} connectorInstanceId={connectorInstanceId} connectorId={connectorId} />
  );
};
